import { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { nanoid } from 'nanoid';

export const ZONE_VISIBILITY_ALL = '__all__';

/** @typedef {'regular' | 'extended'} ZoneKind */

/**
 * @param {string} hex
 * @returns {boolean}
 */
function isValidHexColor(hex) {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

function randomHexColor() {
  const n = Math.floor(Math.random() * 0xffffff);
  return `#${n.toString(16).padStart(6, '0')}`;
}

/**
 * @param {{
 *   zones: Array<{ id: string; name: string; color: string; zoneType: ZoneKind }>;
 *   onZonesChange: (zones: Array<{ id: string; name: string; color: string; zoneType: ZoneKind }>) => void;
 *   zoneVisibilitySelection: string[];
 *   onZoneVisibilitySelectionChange: (next: string[]) => void;
 *   drawTargetZoneId: string | null;
 *   onDrawTargetZoneIdChange: (id: string | null) => void;
 *   onDeleteZonesRequest: (zoneIds: string[]) => void;
 *   drawingToolbar: import('react').ReactNode;
 *   zipBulkImportToolbar?: import('react').ReactNode;
 *   onSaveDrawSession: () => void;
 *   hasUnsavedZoneModelChanges: boolean;
 *   getPortalContainer?: () => HTMLElement;
 * }} props
 */
export default function ZoneControls({
  zones,
  onZonesChange,
  zoneVisibilitySelection,
  onZoneVisibilitySelectionChange,
  drawTargetZoneId,
  onDrawTargetZoneIdChange,
  onDeleteZonesRequest,
  drawingToolbar,
  zipBulkImportToolbar,
  onSaveDrawSession,
  hasUnsavedZoneModelChanges,
  getPortalContainer,
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTargetOpen, setEditTargetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [zoneColor, setZoneColor] = useState('#1976d2');
  const [zoneType, setZoneType] = useState(/** @type {ZoneKind} */ 'regular');
  const [deleteSelection, setDeleteSelection] = useState(
    /** @type {string[]} */ [],
  );

  const visibilityOptions = useMemo(
    () => [
      { id: ZONE_VISIBILITY_ALL, label: 'All service areas' },
      ...zones.map(z => ({ id: z.id, label: z.name })),
    ],
    [zones],
  );

  const handleOpenCreate = useCallback(() => {
    setZoneName('');
    setZoneColor('#1976d2');
    setZoneType('regular');
    setCreateOpen(true);
  }, []);

  const handleGenerateColor = useCallback(() => {
    setZoneColor(randomHexColor());
  }, []);

  const handleSaveNewZone = useCallback(() => {
    const name = zoneName.trim();
    if (!name) return;
    const color = isValidHexColor(zoneColor) ? zoneColor : '#1976d2';
    const id = nanoid();
    const next = [...zones, { id, name, color, zoneType }];
    onZonesChange(next);
    onDrawTargetZoneIdChange(id);
    onZoneVisibilitySelectionChange([id]);
    setCreateOpen(false);
  }, [
    zoneName,
    zoneColor,
    zoneType,
    zones,
    onZonesChange,
    onDrawTargetZoneIdChange,
    onZoneVisibilitySelectionChange,
  ]);

  const handleVisibilityChange = useCallback(
    (_e, newValue) => {
      const ids = newValue.map(v => v.id);
      const prevHadAll = zoneVisibilitySelection.includes(ZONE_VISIBILITY_ALL);

      if (ids.includes(ZONE_VISIBILITY_ALL)) {
        const onlyAll = ids.length === 1 && ids[0] === ZONE_VISIBILITY_ALL;
        if (onlyAll) {
          onZoneVisibilitySelectionChange([ZONE_VISIBILITY_ALL]);
          return;
        }
        if (!prevHadAll && ids.length > 1) {
          onZoneVisibilitySelectionChange([ZONE_VISIBILITY_ALL]);
          return;
        }
        if (prevHadAll) {
          const withoutAll = ids.filter(id => id !== ZONE_VISIBILITY_ALL);
          onZoneVisibilitySelectionChange(
            withoutAll.length ? withoutAll : [ZONE_VISIBILITY_ALL],
          );
          return;
        }
      }
      onZoneVisibilitySelectionChange(ids.length ? ids : [ZONE_VISIBILITY_ALL]);
    },
    [onZoneVisibilitySelectionChange, zoneVisibilitySelection],
  );

  /** Chips: only "All" when all-zones mode; otherwise one chip per selected zone. */
  const autocompleteDisplayValue = useMemo(() => {
    if (zoneVisibilitySelection.includes(ZONE_VISIBILITY_ALL)) {
      const allOpt = visibilityOptions.find(o => o.id === ZONE_VISIBILITY_ALL);
      return allOpt ? [allOpt] : [];
    }
    return visibilityOptions.filter(o =>
      zoneVisibilitySelection.includes(o.id),
    );
  }, [visibilityOptions, zoneVisibilitySelection]);

  const isOptionChecked = useCallback(
    option => {
      if (option.id === ZONE_VISIBILITY_ALL) {
        return zoneVisibilitySelection.includes(ZONE_VISIBILITY_ALL);
      }
      return (
        zoneVisibilitySelection.includes(ZONE_VISIBILITY_ALL) ||
        zoneVisibilitySelection.includes(option.id)
      );
    },
    [zoneVisibilitySelection],
  );

  const handleRequestDeleteZones = useCallback(() => {
    if (deleteSelection.length === 0) return;
    setConfirmOpen(true);
  }, [deleteSelection.length]);

  const handleConfirmDelete = useCallback(() => {
    onDeleteZonesRequest(deleteSelection);
    setDeleteSelection([]);
    setConfirmOpen(false);
    setDeleteOpen(false);
  }, [deleteSelection, onDeleteZonesRequest]);

  const toggleDeleteZone = useCallback(id => {
    setDeleteSelection(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  }, []);

  const handleChooseEditZone = useCallback(
    zoneId => {
      onDrawTargetZoneIdChange(zoneId);
      onZoneVisibilitySelectionChange([zoneId]);
      setEditTargetOpen(false);
    },
    [onDrawTargetZoneIdChange, onZoneVisibilitySelectionChange],
  );

  const editTargetZone = useMemo(
    () => zones.find(z => z.id === drawTargetZoneId) ?? null,
    [zones, drawTargetZoneId],
  );

  const dialogPortalProps = useMemo(
    () =>
      typeof getPortalContainer === 'function'
        ? { container: getPortalContainer }
        : {},
    [getPortalContainer],
  );

  const autocompleteSlotProps = useMemo(
    () =>
      typeof getPortalContainer === 'function'
        ? { popper: { container: getPortalContainer } }
        : undefined,
    [getPortalContainer],
  );

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 12,
          right: 12,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}>
        <Paper
          elevation={3}
          sx={{
            px: 1,
            py: 0.75,
            width: 'fit-content',
            maxWidth: 'min(100%, calc(100vw - 24px))',
            alignSelf: 'center',
          }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            columnGap={1}
            rowGap={1}
            sx={{ width: 'fit-content', maxWidth: '100%' }}>
            <Button variant="contained" size="small" onClick={handleOpenCreate}>
              Create Area
            </Button>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ alignSelf: 'stretch' }}
            />
            {editTargetZone && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ maxWidth: { xs: '100%', sm: 420 } }}>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span">
                    Editing area:
                  </Typography>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0.5,
                      bgcolor: editTargetZone.color,
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                  <Typography
                    variant="body2"
                    component="span"
                    noWrap
                    title={`${editTargetZone.name} · ${editTargetZone.zoneType} · ${editTargetZone.color}`}
                    sx={{ maxWidth: { xs: 140, sm: 200 } }}>
                    {editTargetZone.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                    sx={{ textTransform: 'capitalize' }}>
                    ({editTargetZone.zoneType})
                  </Typography>
                </Stack>
                {hasUnsavedZoneModelChanges && (
                  <Button
                    variant="contained"
                    size="small"
                    color="warning"
                    onClick={onSaveDrawSession}>
                    Save changes
                  </Button>
                )}
              </Stack>
            )}
            {editTargetZone && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ alignSelf: 'stretch' }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setEditTargetOpen(true)}
              disabled={zones.length === 0}>
              Edit new Area
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => {
                setDeleteSelection([]);
                setDeleteOpen(true);
              }}
              disabled={zones.length === 0}>
              Delete Area
            </Button>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ alignSelf: 'stretch' }}
            />
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                flex: '0 1 auto',
                minWidth: 0,
                maxWidth: '100%',
              }}>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}>
                Visible service areas
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={visibilityOptions}
                value={autocompleteDisplayValue}
                onChange={handleVisibilityChange}
                getOptionLabel={o => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disableCloseOnSelect
                {...(autocompleteSlotProps
                  ? { slotProps: autocompleteSlotProps }
                  : {})}
                sx={{ minWidth: 160, maxWidth: 280, flex: '1 1 auto' }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={isOptionChecked(option)}
                    />
                    {option.label}
                  </li>
                )}
                renderInput={params => <TextField {...params} hiddenLabel />}
              />
            </Stack>
          </Stack>
        </Paper>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1,
          maxHeight: 'calc(100% - 100px)',
          overflowY: 'auto',
        }}>
        <Stack direction="column" alignItems="center" spacing={0.75}>
          {drawingToolbar}
          {zipBulkImportToolbar ?? null}
        </Stack>
      </Box>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        {...dialogPortalProps}>
        <DialogTitle>Create Service Area</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Service area name"
              placeholder="e.g. Downtown coverage"
              value={zoneName}
              onChange={e => setZoneName(e.target.value)}
              fullWidth
              required
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Service area color
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  value={zoneColor}
                  onChange={e => setZoneColor(e.target.value)}
                  placeholder="#1976d2"
                  sx={{ width: 120 }}
                />
                <input
                  type="color"
                  value={isValidHexColor(zoneColor) ? zoneColor : '#1976d2'}
                  onChange={e => setZoneColor(e.target.value)}
                  aria-label="Pick service area color"
                  style={{
                    width: 40,
                    height: 36,
                    padding: 0,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleGenerateColor}>
                  Generate
                </Button>
              </Stack>
            </Box>
            <FormControl>
              <FormLabel>Service area type</FormLabel>
              <RadioGroup
                row
                value={zoneType}
                onChange={e =>
                  setZoneType(/** @type {ZoneKind} */ e.target.value)
                }>
                <FormControlLabel
                  value="regular"
                  control={<Radio />}
                  label="Regular"
                />
                <FormControlLabel
                  value="extended"
                  control={<Radio />}
                  label="Extended"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNewZone}>
            Save Service Area
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editTargetOpen}
        onClose={() => setEditTargetOpen(false)}
        fullWidth
        maxWidth="sm"
        {...dialogPortalProps}>
        <DialogTitle>Edit Service Area</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose which service area new shapes are drawn into.
          </Typography>
          <List dense>
            {zones.map(z => (
              <ListItem key={z.id} disablePadding>
                <ListItemButton
                  onClick={() => handleChooseEditZone(z.id)}
                  selected={drawTargetZoneId === z.id}>
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    <Radio
                      edge="start"
                      checked={drawTargetZoneId === z.id}
                      tabIndex={-1}
                      disableRipple
                      sx={{ pointerEvents: 'none' }}
                      inputProps={{ 'aria-hidden': true }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={z.name}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: 0.5,
                            bgcolor: z.color,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Typography component="span" variant="caption">
                          {z.zoneType}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTargetOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
        {...dialogPortalProps}>
        <DialogTitle>Delete service areas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select one or more service areas to delete.
          </Typography>
          <List dense>
            {zones.map(z => (
              <ListItem key={z.id} disablePadding>
                <ListItemButton onClick={() => toggleDeleteZone(z.id)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={deleteSelection.includes(z.id)}
                      tabIndex={-1}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={z.name}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: 0.5,
                            bgcolor: z.color,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Typography component="span" variant="caption">
                          {z.zoneType}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteSelection.length === 0}
            onClick={handleRequestDeleteZones}>
            Delete selected service areas
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        {...dialogPortalProps}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            Remove {deleteSelection.length} service area
            {deleteSelection.length === 1 ? '' : 's'} and all shapes in
            {deleteSelection.length === 1
              ? ' that service area'
              : ' those service areas'}
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
